<script lang="ts">
export default {
  name: 'FavoriteIndicator',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { Star } from '@lucide/vue';

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    disabled?: boolean;
    loading?: boolean;
    onlyOn?: boolean;
    small?: boolean;
    clickable?: boolean;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left' | undefined;
    size?: 'sm' | 'md' | 'lg' | undefined;
  }>(),
  {
    modelValue: false,
    disabled: false,
    loading: false,
    onlyOn: false,
    small: false,
    clickable: true,
    tooltipPosition: 'bottom',
    size: 'md',
  },
);

const classList = computed(() => {
  const classList = {
    disabled: props.disabled,
    small: props.small,
    clickable: props.clickable,
  } as Record<string, boolean>;

  if (props.tooltipPosition) {
    classList[tooltipPosition.value] = props.clickable;
  }

  return classList;
});

const tooltipPosition = computed(() => {
  if (props.tooltipPosition) {
    return `tooltip tooltip-${props.tooltipPosition}`;
  }
  return '';
});

const iconSize = computed(() => {
  switch (props.size) {
    case 'sm':
      return 13;
    case 'md':
      return 16;
    case 'lg':
      return 19;
    default:
      return 16;
  }
});

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'size-6';
    case 'md':
      return 'size-8';
    case 'lg':
      return 'size-10';
    default:
      return 'size-8';
  }
});

function clicked() {
  emit('update:modelValue', !props.modelValue);
}
</script>

<template>
  <div
    class="group root flex items-center justify-center hover:cursor-pointer"
    :class="[classList, sizeClass]"
    @click="clicked"
    data-test="favorite-icon"
    :data-tip="props.clickable ? 'Toggle Favorite' : undefined">
    <div
      v-if="props.modelValue"
      class="on text-md flex items-center text-primary">
      <Star
        :size="iconSize"
        class="fill-gold stroke-primary stroke-1" />
    </div>
    <div
      v-else
      class="off flex items-center text-lg"
      :class="{ hide: onlyOn }">
      <Star
        :size="iconSize"
        class="stroke-gray-400 group-hover:stroke-gray-500" />
    </div>
  </div>
</template>

<style scoped>
/*noinspection CssUnusedSymbol*/
.root.disabled {
  cursor: default;
  color: var(--v-gray-base);
  opacity: 0.5;
}

.root > div {
  font-size: 1.1rem;
}

.root.small > div {
  font-size: 0.8rem;
}

.root.clickable:hover > div {
  transform: scale(1.1);
}

.off {
  visibility: visible;
}

.off.hide {
  visibility: hidden;
}
</style>
